import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { viewReviewsSummary } from '../../slice/reviewSlice'
import { useLocation, useNavigate } from 'react-router-dom'

const STAR_KEYS = [
    { key: 'five_star', label: '5 Stars', value: 5 },
    { key: 'four_star', label: '4 Stars', value: 4 },
    { key: 'three_star', label: '3 Stars', value: 3 },
    { key: 'two_star', label: '2 Stars', value: 2 },
    { key: 'one_star', label: '1 Star', value: 1 },
]

const StarRating = ({ rating, size = 16 }) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {[1, 2, 3, 4, 5].map(star => (
            <i
                key={star}
                className="fe-star"
                style={{
                    fontSize: `${size}px`,
                    color: star <= rating ? '#ffc107' : '#dee2e6',
                    filter: star <= rating ? 'none' : 'opacity(0.4)',
                }}
            />
        ))}
    </div>
)

const TutorReviewSummary = () => {
    const dispatch = useDispatch()
    const navigate = useNavigate()
    const location = useLocation()

    const tutorId = location.state?.tutorId

    const { reviewSum } = useSelector(state => state.review)
    const data = reviewSum?.data

    useEffect(() => {
        if (!tutorId) {
            navigate('/reviews')
            return
        }
        dispatch(viewReviewsSummary({ tutor_id: tutorId }))
    }, [tutorId, dispatch, navigate])

    if (!data) {
        return (
            <div className="text-center py-5">
                <i className="fe-alert-circle" style={{ fontSize: '48px', color: '#dc3545' }}></i>
                <h5 className="mt-3">No data found</h5>
                <button className="btn btn-primary mt-3" onClick={() => navigate('/reviews')}>
                    Go Back to Reviews
                </button>
            </div>
        )
    }

    const { tutor, average_rating, total_reviews, rating_breakdown } = data

    return (
        <div className="container-fluid px-4 py-3">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h4 className="fw-semibold mb-0">Tutor Rating Summary</h4>
                <button
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                    style={{ padding: '8px 20px', fontSize: '14px', borderRadius: '6px' }}
                >
                    ← Back
                </button>
            </div>

            {/* Tutor Profile Card */}
            <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: '8px' }}>
                <div className="card-body p-4 d-flex align-items-center gap-4 flex-wrap">
                    <img
                        src={tutor?.profile_picture || '/assets/avatar.png'}
                        alt="Tutor"
                        onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/assets/avatar.png' }}
                        style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            objectFit: 'cover',
                            border: '3px solid #f8f9fa',
                            flexShrink: 0,
                        }}
                    />
                    <div>
                        <h5 className="mb-1 fw-semibold">
                            {`${tutor?.first_name || ''} ${tutor?.last_name || ''}`.trim() || 'NA'}
                        </h5>
                        <p className="mb-2 text-muted" style={{ fontSize: '14px' }}>{tutor?.email || 'NA'}</p>
                        <div className="d-flex gap-2 flex-wrap align-items-center">
                            {tutor?.is_premium_verified === 1 && (
                                <span className="badge bg-warning text-dark">Premium Verified</span>
                            )}
                            {tutor?.is_mohre_verification === 1 && (
                                <span className="badge bg-primary">MoHRE Verified</span>
                            )}
                            {tutor?.badges?.length > 0 && tutor.badges.map((badge, i) => (
                                <span key={i} className="badge bg-info text-dark">{badge}</span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                {/* Overall Rating */}
                <div className="col-md-4 mb-4">
                    <div className="card border-0 shadow-sm h-100 text-center" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Overall Rating
                        </div>
                        <div className="card-body d-flex flex-column align-items-center justify-content-center py-4">
                            <div style={{ fontSize: '64px', fontWeight: '700', color: '#212529', lineHeight: 1 }}>
                                {Number(average_rating).toFixed(1)}
                            </div>
                            <div className="my-3">
                                <StarRating rating={Math.round(average_rating)} size={22} />
                            </div>
                            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                Based on <strong>{total_reviews}</strong> {total_reviews === 1 ? 'review' : 'reviews'}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Rating Breakdown */}
                <div className="col-md-8 mb-4">
                    <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '8px' }}>
                        <div className="card-header bg-light fw-semibold border-0" style={{ borderRadius: '8px 8px 0 0' }}>
                            Rating Breakdown
                        </div>
                        <div className="card-body py-3">
                            {STAR_KEYS.map(({ key, label, value }) => {
                                const item = rating_breakdown?.[key] || { count: 0, percentage: 0 }
                                return (
                                    <div key={key} className="d-flex align-items-center gap-3 mb-3">
                                        {/* Star label */}
                                        <div style={{ width: '60px', flexShrink: 0 }}>
                                            <StarRating rating={value} size={13} />
                                        </div>

                                        {/* Progress bar */}
                                        <div style={{ flex: 1 }}>
                                            <div
                                                style={{
                                                    height: '10px',
                                                    borderRadius: '999px',
                                                    backgroundColor: '#f1f3f5',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                <div
                                                    style={{
                                                        height: '100%',
                                                        width: `${item.percentage}%`,
                                                        borderRadius: '999px',
                                                        backgroundColor: item.percentage > 0 ? '#ffc107' : '#dee2e6',
                                                        transition: 'width 0.4s ease',
                                                    }}
                                                />
                                            </div>
                                        </div>

                                        {/* Count + percentage */}
                                        <div style={{ width: '80px', flexShrink: 0, textAlign: 'right' }}>
                                            <span style={{ fontSize: '13px', color: '#495057' }}>
                                                {item.count} <span style={{ color: '#adb5bd' }}>({item.percentage}%)</span>
                                            </span>
                                        </div>
                                    </div>
                                )
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default TutorReviewSummary